import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { ButtonRounded } from '../UISystem/Buttons';
import { SHADOWS } from '../../styles/UISystem';

/**
 * IosHandoffSheet — the instruction sheet raised BEFORE handing off to an iOS
 * system sheet, pointing at the control that actually completes the action.
 *
 * iOS does the same misdirection twice: the bright blue checkmark at the top
 * of a vCard preview or a .ics preview reads as "done" and saves nothing. The
 * real save is a quieter control — "Create New Contact" below the fold, or the
 * floating "Add to calendar" pill — and people tap the loud one and believe
 * they finished. Same trap, so the same signpost: this sheet names the target,
 * [Ok] opens the real thing.
 *
 * WHY A SECOND TAP, rather than showing this for a beat and opening the system
 * sheet automatically: the handoff is a programmatic anchor click on a blob
 * URL, which iOS Safari only honours inside a user gesture's activation window
 * — about a second. A message readable in under a second is not a message, and
 * the system sheet would cover it at the exact moment it became relevant.
 * [Ok] IS the gesture, and it keeps the instruction on screen until the person
 * has read it.
 *
 * iOS-only by convention: Android's download lands in the right app on its
 * own, so callers gate on isIOS() rather than this component doing it.
 *
 * `rows` are the WORDS TO LOOK FOR, verbatim in iOS's own capitalisation —
 * quoted, not drawn. No button shapes, no container: a convincing facsimile of
 * a control is a dead control, and someone taps it, nothing happens, and they
 * are confused before they even reach the real screen (user ruling 2026-08-16,
 * choosing words over a mimicked button; it also stops the sheet from claiming
 * iOS shows a layout it doesn't). A hairline separates rows and nothing
 * encloses them.
 *
 * `footnote` is a step further on — a screen after the one `rows` name — so it
 * sits below, visually apart.
 *
 * No scrim — elevation plus an invisible tap-outside layer, per
 * ui-design-system §4.8 (a backdrop tints Safari's toolbars).
 *
 * Extracted 2026-08-16 from `SaveToPhoneSheet` (its only caller then) when the
 * calendar handoff needed the identical sheet.
 */
const IosHandoffSheet = ({ headline, rows, footnote = null, okLabel = 'Ok', onOk, onClose }) => {
  // Body scroll lock while the sheet is open (ui-design-system §4.7)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 1999, background: 'transparent' }}
      />

      <motion.div
        initial={{ y: 400 }}
        animate={{ y: 0 }}
        exit={{ y: 400 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2000,
          backgroundColor: 'white',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: SHADOWS.TIER3,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '18px',
            padding: '28px 24px',
            paddingBottom: 'calc(28px + env(safe-area-inset-bottom))',
            maxWidth: '340px',
            margin: '0 auto',
          }}
        >
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#000000' }}>{headline}</div>

          <div style={{ width: '100%' }}>
            {rows.map((row, i) => (
              <div
                key={row}
                style={{
                  padding: '12px 8px',
                  fontSize: '17px',
                  color: '#1C1C1E',
                  textAlign: 'center',
                  borderBottom: i < rows.length - 1 ? '1px solid #E3E3E7' : 'none',
                }}
              >
                {row}
              </div>
            ))}
          </div>

          {/* A step further on than the rows above — visually apart */}
          {footnote && (
            <div
              style={{
                fontSize: '17px',
                color: '#1C1C1E',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {footnote}
            </div>
          )}

          <ButtonRounded text={okLabel} variant="primary" size="large" onClick={onOk} />
        </div>
      </motion.div>
    </>
  );
};

IosHandoffSheet.propTypes = {
  headline: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.string).isRequired,
  footnote: PropTypes.node,
  okLabel: PropTypes.string,
  onOk: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default IosHandoffSheet;
